import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectorRef, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';

import { BillingPlansSelectors } from '@recruitee/billing-plans-data';
import { filterBoolean } from '@recruitee/common';
import { InfoboxComponent } from '@recruitee/components/infobox';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { IntercomRecipients, IntercomService } from '@recruitee/intercom-integration';
import { SubscriptionPricingSelectors } from '@recruitee/pricing-data';
import { Currency } from '@recruitee/pricing-types';
import { BillingPlanTier } from '@recruitee/products-types';
import type { BillingPlan } from '@recruitee/products-types';
import { SpamVerificationService } from '@recruitee/spam-verification-logic';
import {
  SpamVerificationDialogService,
  SpamVerificationInfoboxContainerComponent,
} from '@recruitee/spam-verification-smart-ui';
import type { PaymentInterval } from '@recruitee/subscription-types';
import { HeadingComponent } from '@recruitee/user-interface';

import { getProductCodeOrThrow } from '../../helpers/get-product-code-or-throw';
import { getProductName } from '../../helpers/get-product-name';
import { ready } from '../../helpers/rx-operators';
import {
  PricingFeatureFacade,
  PricingFeatureLoadedModel,
  PricingFeatureObservableModel,
} from '../../types/pricing-feature.types';
import { AssignedProductMessageContainerComponent } from '../assigned-product-message/assigned-product-message-container.component';
import { planPicker } from './lib/plan-picker';
import { PlanPickerCompareComponent } from './plan-picker-compare/plan-picker-compare.component';
import { PlanPickerGlobalControlsComponent } from './plan-picker-global-controls/plan-picker-global-controls.component';
import { PlanPickerProductsComponent } from './plan-picker-products/plan-picker-products.component';
import { PlanPickerAnalytics } from './plan-picker.analytics';
import { PlanPickerProduct, PlanPickerState, ProductStatus } from './plan-picker.types';

@Component({
  selector: 'rt-plan-picker',
  templateUrl: './plan-picker.component.html',
  styleUrls: ['./plan-picker.component.less'],
  providers: [PlanPickerAnalytics],
  standalone: true,
  imports: [
    HeadingComponent,
    PlanPickerGlobalControlsComponent,
    NgTemplateOutlet,
    SpamVerificationInfoboxContainerComponent,
    InfoboxComponent,
    PlanPickerProductsComponent,
    PlanPickerCompareComponent,
    AssignedProductMessageContainerComponent,
    PushPipe,
    TranslatePipe,
  ],
})
export class PlanPickerComponent {
  public changeCurrency$ = new Subject<Currency>();
  public changeInterval$ = new Subject<PaymentInterval>();
  public changeSlots$ = new Subject<{ codename: BillingPlanTier; value: number }>();
  public BillingPlanTier = BillingPlanTier;
  public isManualSubscription: boolean = false;
  public state: PlanPickerState;
  public isUpgradingLaunch5$: Observable<boolean>;

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    public facade: PricingFeatureFacade,
    public i18n: I18nService,
    public router: Router,
    public analytics: PlanPickerAnalytics,
    protected cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private intercomService: IntercomService,
    private featureModel: PricingFeatureObservableModel,
    private store: Store,
    private spamVerificationService: SpamVerificationService,
    private spamVerificationDialogService: SpamVerificationDialogService,
  ) {
    this.facade.fetchPlansIfNeeded();
  }

  // eslint-disable-next-line max-lines-per-function
  public ngOnInit() {
    const destroy$ = this.ngOnDestroy$.asObservable();
    const featureModel$ = this.featureModel.pipe(ready, shareReplay(1));
    const pickerState$ = planPicker(
      featureModel$,
      this.changeInterval$,
      this.changeCurrency$,
      this.changeSlots$,
      this.store.select(BillingPlansSelectors.billingPlans).pipe(filterBoolean),
      this.store.select(BillingPlansSelectors.activePlan),
      this.spamVerificationService.selectSpamVerificationStatus$,
      this.store.select(SubscriptionPricingSelectors.data).pipe(
        filterBoolean,
        map(subscription => subscription.addons),
      ),
    ).pipe(shareReplay(1));

    this.isUpgradingLaunch5$ = pickerState$.pipe(
      map(
        state =>
          state.ownedSlots === 5 &&
          state.activeProduct.tier === BillingPlanTier.Launch &&
          state.activeProduct.baseSlots === 5,
      ),
    );

    combineLatest([pickerState$, featureModel$])
      .pipe(takeUntil(destroy$))
      // eslint-disable-next-line max-lines-per-function
      .subscribe(([state, model]) => {
        this.isManualSubscription = !!model.subscription?.manual;
        this.state = state;

        this.setAnalyticsProps(state, model);

        this.cdRef.detectChanges();
      });
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public continue(product: PlanPickerProduct) {
    if (
      this.spamVerificationService.verificationRequired() &&
      product.status !== ProductStatus.ManualService
    ) {
      this.spamVerificationDialogService.open();
      return;
    }

    if (product.status > ProductStatus.Owned) {
      this.navigateToProductPayment(
        product.product!.id,
        product.state.selectedSlots,
        this.state.interval,
      );
    } else {
      this.contactSalesAboutProduct(product.codename);
    }
  }

  public cancelSubscription() {
    if (!this.state.isSelfService) {
      this.contactSalesAboutChurn();
    } else {
      this.router.navigateByUrl(this.facade.getSubscriptionChurnPageUrl().toString());
    }
  }

  public contactSalesAboutChurn() {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.churn'),
      IntercomRecipients.Sales,
    );
  }

  public contactSalesAboutProduct(codename: BillingPlanTier) {
    this.intercomService.startNewMessage(
      this.i18n.translate('app.pricing.plan_picker.intercom.interested_in', {
        planName: getProductName(
          {
            codename,
          },
          this.i18n,
          true,
        ),
      }),
      IntercomRecipients.Sales,
    );
  }

  public trackByIndex(index: number) {
    return index;
  }

  public get codename(): BillingPlanTier {
    return this.state.activeProduct.tier;
  }

  public get assignedProductInterval(): PaymentInterval {
    return this.state.assignedProduct!.billingPeriod;
  }

  public openPlanPicker2024() {
    this.facade.openPlanPicker();
  }

  private navigateToProductPayment(
    productId: BillingPlan['id'],
    selectedSlots: number,
    selectedInterval: PaymentInterval,
  ) {
    this.router.navigate([productId, selectedSlots, selectedInterval, 'billing'], {
      relativeTo: this.activatedRoute.parent,
    });
  }

  private setAnalyticsProps(state: PlanPickerState, model: PricingFeatureLoadedModel) {
    this.analytics.setCommonProps({
      assignedProductCode: state.assignedProduct
        ? getProductCodeOrThrow(state.assignedProduct?.tier)
        : undefined,
      slotsLeft: model.trial ? undefined : model.slotsTotal - model.jobSlotsUsed,
      trialDaysLeft: model.trial
        ? moment(model.trial.untilDate).diff(moment(), 'd', false)
        : undefined,
      trial: !!model.trial,
      activePlanCode: getProductCodeOrThrow(state.activeProduct?.tier),
      activePlanCurrency: Currency[state.activeProduct.currency],
      activePlanCustom: state.activeProduct.custom,
      activePlanPaymentInterval: model.subscription?.billingPeriod,
      usedJobSlots: model.jobSlotsUsed,
      selfService: state.isSelfService,
    });
  }
}
